/* ==========================================================================
   bb-saved.js — Beyond Bohemian "Saved Pieces"
   --------------------------------------------------------------------------
   Local-first wishlist. Everything works with zero backend: the list lives in
   localStorage and survives refreshes, tabs, and sessions on that device.

   When BBSaved.sync is enabled (Phase 2), the same list round-trips through
   an app proxy at /apps/saved, which writes it onto the customer's own
   `custom.saved_products` metafield. The data therefore lives in Shopify,
   not in a vendor database — see the app proxy worker for the write path.

   No dependencies. No build step. Safe to load with `defer`.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'bb:saved';
  var EVT = 'bb:saved:change';
  var MAX = 200; // generous ceiling; guards against runaway localStorage

  /* ---------- storage ---------------------------------------------------- */

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      return Array.isArray(list) ? list.filter(function (i) { return i && i.handle; }) : [];
    } catch (e) {
      return []; // private mode, quota, or corrupt payload — degrade to empty
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (e) { /* private mode: in-memory only for this pageview */ }
    cache = list;
    document.dispatchEvent(new CustomEvent(EVT, { detail: { list: list } }));
  }

  var cache = read();

  /* ---------- public surface --------------------------------------------- */

  var API = {
    all: function () { return cache.slice(); },
    handles: function () { return cache.map(function (i) { return i.handle; }); },
    count: function () { return cache.length; },
    has: function (handle) {
      return cache.some(function (i) { return i.handle === handle; });
    },
    add: function (handle, meta) {
      if (!handle || API.has(handle)) return false;
      var entry = { handle: handle, savedAt: Date.now() };
      if (meta && meta.title) entry.title = meta.title;
      write([entry].concat(cache));
      API.push();
      return true;
    },
    remove: function (handle) {
      if (!handle) return false;
      write(cache.filter(function (i) { return i.handle !== handle; }));
      API.push();
      return true;
    },
    toggle: function (handle, meta) {
      return API.has(handle) ? (API.remove(handle), false) : (API.add(handle, meta), true);
    },
    clear: function () { write([]); API.push(); },

    /* ---------- Phase 2 sync (no-op until an endpoint is configured) ------ */

    sync: null,   // set to '/apps/saved' by bb-saved-init.liquid when enabled
    customer: null,

    /* Merge the server list into the local one, newest-wins per handle. */
    pull: function () {
      if (!API.sync || !API.customer) return Promise.resolve(cache);
      return fetch(API.sync, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || !Array.isArray(data.items)) return cache;
          var seen = Object.create(null);
          var merged = [];
          cache.concat(data.items).forEach(function (i) {
            if (!i || !i.handle || seen[i.handle]) return;
            seen[i.handle] = true;
            merged.push(i);
          });
          merged.sort(function (a, b) { return (b.savedAt || 0) - (a.savedAt || 0); });
          write(merged);
          return merged;
        })
        .catch(function () { return cache; }); // offline / proxy down: local list stands
    },

    /* Debounced write-through so rapid toggling is one request, not ten. */
    push: (function () {
      var t = null;
      return function () {
        if (!API.sync || !API.customer) return;
        clearTimeout(t);
        t = setTimeout(function () {
          fetch(API.sync, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: cache })
          }).catch(function () { /* local list is the source of truth meanwhile */ });
        }, 600);
      };
    })()
  };

  window.BBSaved = API;

  /* ---------- DOM wiring -------------------------------------------------- */

  function paintButton(btn) {
    var handle = btn.getAttribute('data-handle');
    var on = API.has(handle);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', (on ? 'Remove ' : 'Save ') + (btn.getAttribute('data-title') || 'this item') + (on ? ' from' : ' to') + ' your saved items');
    btn.classList.toggle('is-saved', on);
  }

  function paintAll() {
    document.querySelectorAll('.bb-saved-heart').forEach(paintButton);
    var n = API.count();
    document.querySelectorAll('[data-bb-saved-count]').forEach(function (el) {
      el.textContent = n > 0 ? String(n) : '';
      el.hidden = n === 0;
    });
    /* Fill the header heart once anything is saved, so the state reads at a
       glance even where the count bubble is clipped by a tight header. */
    document.querySelectorAll('.bb-saved-link').forEach(function (el) {
      el.classList.toggle('has-items', n > 0);
    });
  }

  /* Delegated: works for cards rendered now and for anything injected later
     by pagination, filtering, or the quick-add drawer. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.bb-saved-heart');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();   // never let the card's stretched link swallow this
    var handle = btn.getAttribute('data-handle');
    if (!handle) return;
    var nowOn = API.toggle(handle, { title: btn.getAttribute('data-title') });
    btn.classList.remove('bb-pop');
    void btn.offsetWidth;             // restart the animation
    if (nowOn) {
      btn.classList.add('bb-pop');
      toast('Saved to your items.', API.count());
    }
  });

  /* ---------- save confirmation ------------------------------------------
     Filling the heart tells you it worked but not where it went. A brief
     toast names the destination and links to it, which is the whole gap
     between "I favourited something" and "I can find my favourites".

     Appended to <body> deliberately: the product grid sets
     `container-type: inline-size`, which would make it the containing block
     for a position:fixed descendant and strand the toast inside a card. */
  var toastEl = null, toastTimer = null;

  function toast(message, count) {
    if (!document.body) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'bb-saved-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    var text = document.createElement('span');
    text.className = 'bb-saved-toast__text';
    text.textContent = message;
    var link = document.createElement('a');
    link.className = 'bb-saved-toast__link';
    link.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'pages/saved';
    link.textContent = count === 1 ? 'View your item' : 'View your ' + count + ' items';
    toastEl.appendChild(text);
    toastEl.appendChild(link);
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      if (toastEl) toastEl.classList.remove('is-visible');
    }, 4200);
  }

  /* Public repaint hook. State is painted on load and on change, but NOT when
     new DOM arrives — and the Goop quick-shop drawer injects cached HTML from
     a JS Map, so a panel fetched before a save would show a stale heart.
     Anything that injects markup containing a heart must call this:

         window.BBSaved && window.BBSaved.repaint();

     Clicks need no such call: the handler is delegated on `document`, so it
     already covers injected DOM. */
  API.repaint = paintAll;

  document.addEventListener(EVT, paintAll);
  document.addEventListener('DOMContentLoaded', function () {
    paintAll();
    API.pull().then(paintAll);
  });
  if (document.readyState !== 'loading') { paintAll(); API.pull().then(paintAll); }

  /* Another tab changed the list — stay consistent without a refresh. */
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { cache = read(); paintAll(); }
  });
})();
